<template>
    <div v-if="hasContent">
        <div
            ref="contentRef"
            class="overflow-hidden transition-all duration-300"
            :class="showFullContent ? '' : maxHeightClass"
            v-html="content"
        ></div>
        <button
            v-if="contentNeedsToggle"
            @click="showFullContent = !showFullContent"
            class="text-primary hover:underline text-sm mt-2 flex items-center gap-x-1"
        >
            <span>{{ showFullContent ? 'Show less' : 'Show more' }}</span>
            <font-awesome
                :icon="['fas', showFullContent ? 'angle-up' : 'angle-down']"
                class="h-3 w-3"
            />
        </button>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useHtmlUtils } from '~/composables/useHtmlUtils';

const props = defineProps({
    content: {
        type: String,
        default: ''
    },
    maxHeight: {
        type: Number,
        default: 128  // Default to 128px (8rem or max-h-32 in Tailwind)
    }
});

const showFullContent = ref(false);
const contentNeedsToggle = ref(false);
const contentRef = ref(null);

const { hasHtmlContent } = useHtmlUtils();
const hasContent = computed(() => hasHtmlContent(props.content));

// Convert pixel height to Tailwind class
const maxHeightClass = computed(() => {
    // Map common pixel values to Tailwind classes
    const heightMap = {
        32: 'max-h-8',
        64: 'max-h-16',
        96: 'max-h-24',
        128: 'max-h-32',
        160: 'max-h-40',
        192: 'max-h-48',
        224: 'max-h-56',
        256: 'max-h-64'
    };
    
    return heightMap[props.maxHeight] || 'max-h-32';
});

const checkContentHeight = async () => {
    await nextTick();
    if (contentRef.value) {
        const element = contentRef.value;
        const actualHeight = element.scrollHeight;
        contentNeedsToggle.value = actualHeight > props.maxHeight;
    }
};

onMounted(() => {
    checkContentHeight();
});

watch(() => props.content, () => {
    checkContentHeight();
});
</script>